/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GenAILiveClient } from "../../lib/genai-live-client";
import { LiveClientOptions } from "../../types";
import { AudioStreamer } from "../../lib/audio-streamer";
import { audioContext } from "../../lib/utils";
import VolMeterWorket from "../../lib/worklets/vol-meter";
import { LiveConnectConfig, LiveServerToolCall, Modality, EndSensitivity } from "@google/genai";
import { get_first_stage_instructions, get_stage_instructions } from "../../lib/conversation-config";
import { GEMINI_MODEL } from "../../config";
import {
  endConversationDeclaration,
  getStageInstructionsDeclaration,
  makeNoteDeclaration,
  suggestJobsDeclaration,
} from "../../lib/tools";

const consoleOff = process.env.REACT_APP_FRONTEND_CONSOLE_OFF === "true";

if (!consoleOff) {
  console.log("DEBUG: Module loaded. Imported GEMINI_MODEL:", GEMINI_MODEL);
}

export type UseLiveAPIResults = {
  client: GenAILiveClient;
  setConfig: (config: LiveConnectConfig) => void;
  config: LiveConnectConfig;
  model: string;
  setModel: (model: string) => void;
  connected: boolean;
  connect: (
    language: string,
    countryCode: string,
    voiceName: string,
    apiKey: string,
  ) => Promise<void>;
  disconnect: () => Promise<void>;
  volume: number;
  isResuming: boolean;
  isResumedSession: boolean;
  confirmEndConversation: (shouldEnd: boolean) => void;
  resume: () => Promise<void>;
};

export function useLiveAPI(options: LiveClientOptions): UseLiveAPIResults {
  const optionsRef = useRef(options);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const client = useMemo(() => {
    const proxyOptions: LiveClientOptions = {
      ...optionsRef.current, // Copy initial config
      onJobSuggestions: async () => {
        if (optionsRef.current.onJobSuggestions) {
          return await optionsRef.current.onJobSuggestions();
        }
        return [];
      },
      onNoteMade: (note) => {
        if (optionsRef.current.onNoteMade) {
          optionsRef.current.onNoteMade(note);
        }
      },
    };
    return new GenAILiveClient(proxyOptions);
  }, []); // Empty dependency array to ensure singleton client

  const audioStreamerRef = useRef<AudioStreamer | null>(null);
  const [currentStage, setCurrentStage] = useState(0);
  const [countryCode, setCountryCode] = useState("PL");
  const [language, setLanguage] = useState("English");

  const [model, setModel] = useState<string>(
    GEMINI_MODEL
  );


  const [config, setConfig] = useState<LiveConnectConfig>({});
  const [connected, setConnected] = useState(false);
  const [volume, setVolume] = useState(0);
  const [isEndingConversation, setIsEndingConversation] = useState(false);

  // Session Resumption State
  const [sessionResumptionToken, setSessionResumptionToken] = useState<string | null>(null);
  const [conversationStartTime, setConversationStartTime] = useState<number | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [voiceName, setVoiceName] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);
  const [isResumedSession, setIsResumedSession] = useState(false);
  const [pendingEndContext, setPendingEndContext] = useState<LiveServerToolCall | null>(null);

  const retryCountRef = useRef(0);
  const connectionStableTimerRef = useRef<any>(null);

  const disconnect = useCallback(async () => {
    audioStreamerRef.current?.stop();
    client.disconnect();
    setConnected(false);
    setSessionResumptionToken(null);
    setConversationStartTime(null);
    setPendingEndContext(null);
    setIsEndingConversation(false);
  }, [setConnected, client]);

  useEffect(() => {
    if (isEndingConversation) {
      const endConversation = () => {
        client.emit("conversationend");
        setIsEndingConversation(false);
        if (audioStreamerRef.current) {
          audioStreamerRef.current.onComplete = () => { };
        }
      };

      if (audioStreamerRef.current) {
        if (audioStreamerRef.current.audioQueue.length === 0) {
          endConversation();
        } else {
          audioStreamerRef.current.onComplete = endConversation;
        }
      }
    }
  }, [isEndingConversation, client]);

  // register audio for streaming server -> speakers
  useEffect(() => {
    if (!audioStreamerRef.current) {
      audioContext({ id: "audio-out" }).then((audioCtx: AudioContext) => {
        audioStreamerRef.current = new AudioStreamer(audioCtx);
        audioStreamerRef.current
          .addWorklet<any>("vumeter-out", VolMeterWorket, (ev: any) => {
            setVolume(ev.data.volume);
          })
          .then(() => {
            // Successfully added worklet
          });
      });
    }
  }, [audioStreamerRef]);

  const confirmEndConversation = useCallback((shouldEnd: boolean) => {
    if (pendingEndContext) {
      if (shouldEnd) {
        disconnect();
      }
      setPendingEndContext(null);
      setIsEndingConversation(false);
    }
  }, [pendingEndContext, disconnect]);

  const onToolCall = useCallback(
    async (toolCall: LiveServerToolCall) => {
      if (toolCall.functionCalls) {
        // Handle stage switching
        const stageFc = toolCall.functionCalls.find(
          (fc) => fc.name === getStageInstructionsDeclaration.name
        );
        if (stageFc) {
          const nextStage = currentStage + 1;


          const newInstructions = get_stage_instructions(
            nextStage,
            language,
            countryCode
          );

          if (newInstructions) {
            setCurrentStage(nextStage);
            console.log(
              `DEBUG: Tool call to update system prompt received. Moving to stage ${nextStage + 1
              }}. New instructions:\n${newInstructions}`
            );
            client.sendToolResponse({
              functionResponses: toolCall.functionCalls.map((fc) => ({
                response: { result: newInstructions as string },
                id: fc.id,
                name: fc.name,
              })),
            });
            console.log("DEBUG: Sent new instructions to LLM.");
          } else {
            console.log("DEBUG: All instructions received.");
            client.sendToolResponse({
              functionResponses: toolCall.functionCalls.map((fc) => ({
                response: { result: "All instructions received. Follow all the instructions given and only end the conversation if all instructions have been followed and all questions have been asked." },
                id: fc.id,
                name: fc.name,
              })),
            });
          }
        }

        // Handle note making
        const noteFc = toolCall.functionCalls.find(
          (fc) => fc.name === makeNoteDeclaration.name
        );
        if (noteFc && noteFc.args?.key && noteFc.args?.value) {
          console.log(`DEBUG: Tool call to make note:`, noteFc.args);
          // Use optionsRef to get the latest callback
          if (optionsRef.current.onNoteMade) {
            optionsRef.current.onNoteMade({ [String(noteFc.args.key)]: String(noteFc.args.value) });
          }
          client.sendToolResponse({
            functionResponses: toolCall.functionCalls.map((fc) => ({
              response: { result: "ok" },
              id: fc.id,
              name: fc.name,
            })),
          });
        }

        // Handle job suggestions
        const jobFc = toolCall.functionCalls.find(
          (fc) => fc.name === suggestJobsDeclaration.name
        );
        if (jobFc) {
          console.log(`DEBUG: Tool call to suggest jobs`);
          // Use optionsRef to get the latest callback
          if (optionsRef.current.onJobSuggestions) {
            const jobs = await optionsRef.current.onJobSuggestions();
            if (jobs && jobs.length === 0) {
              client.send({ text: "No job matches were found." });
            }

            let resultMessage = "";
            if (jobs && jobs.length > 0) {
              const topJobs = jobs.slice(0, 3);
              const titles = topJobs.map((job) => job.title);

              let jobListString = "";
              if (titles.length === 1) {
                jobListString = titles[0];
              } else if (titles.length === 2) {
                jobListString = `${titles[0]} and ${titles[1]}`;
              } else {
                const lastTitle = titles.pop();
                jobListString = `${titles.join(", ")}, and ${lastTitle}`;
              }
              resultMessage = `Say: "Here are the job titles found: ${jobListString}. Let's go over them one by one. Use the ‘Interested’ and ‘Not Interested’ buttons to indicate your preference. Afterward, you’ll see the next job suggestion."`;
            }

            console.log("DEBUG: Job tool called.");
            client.sendToolResponse({
              functionResponses: toolCall.functionCalls.map((fc) => ({
                response: {
                  result: resultMessage,
                },
                id: fc.id,
                name: fc.name,
              })),
            });
          }
        }

        // Handle conversation end
        const endFc = toolCall.functionCalls.find(
          (fc) => fc.name === endConversationDeclaration.name
        );
        if (endFc) {
          console.log(`DEBUG: Tool call to end conversation`);
          setPendingEndContext(toolCall);
          setIsEndingConversation(true);
          client.sendToolResponse({
            functionResponses: toolCall.functionCalls.map((fc) => ({
              response: {
                result: "Preparing to end the conversation. Confirming with the user first. If the user does not confirm, continue the conversation as normal.",
              },
              id: fc.id,
              name: fc.name,
            })),
          });
        }
      }
    },
    [client, currentStage, countryCode, language]
  );


  const onSessionResumptionUpdate = useCallback((event: any) => {
    console.log("DEBUG: Session Resumption Update received", event);
    if (event.newHandle) {
      setSessionResumptionToken(event.newHandle);
      // We assume the ID is embedded or we use the latest one, but for now we store the handle
      // The API docs say "session_id" and "resumption_token" but the type might be opaque.
      // We will store the opaque handle.
    }
  }, []);

  const resume = useCallback(async () => {
    if (!sessionResumptionToken || !apiKey || !voiceName || !conversationStartTime) {
      console.log("DEBUG: Cannot resume, missing parameters.");
      setConnected(false);
      return;
    }

    // Check 30 minute limit
    const conversationDuration = (Date.now() - conversationStartTime) / 1000 / 60; // in minutes
    if (conversationDuration > 30) {
      console.log("DEBUG: Conversation limit exceeded (30 mins). Ending session.");
      disconnect();
      return;
    }

    console.log("DEBUG: Resuming session with token:", sessionResumptionToken);
    setResuming(true);
    setIsResumedSession(true);

    const updatedInstructions = get_first_stage_instructions(language, countryCode);

    const resumeConfig: LiveConnectConfig = {
      responseModalities: [Modality.AUDIO],
      realtimeInputConfig: {
        automaticActivityDetection: {
          endOfSpeechSensitivity: EndSensitivity.END_SENSITIVITY_LOW,
          silenceDurationMs: 300,
        },
      },
      sessionResumption: {
        handle: sessionResumptionToken
      },
      inputAudioTranscription: {},
      outputAudioTranscription: {},
      systemInstruction: { parts: [{ text: updatedInstructions }] },
      tools: [
        {
          functionDeclarations: [
            getStageInstructionsDeclaration,
            makeNoteDeclaration,
            suggestJobsDeclaration,
            endConversationDeclaration,
          ],
        },
      ],
      speechConfig: {
        voiceConfig: { prebuiltVoiceConfig: { voiceName: voiceName } },
      },
    };

    setConfig(resumeConfig);
    // Note: client.disconnect() might clear the state, so we need to be careful.
    // However, we are "reconnecting" logically.
    // The previous connection is likely dead or dying.
    console.log("DEBUG: Disconnecting before resume...");
    client.disconnect(false); // Do not mark session as complete in DB
    console.log("DEBUG: Connecting to model: ", model)
    try {
      const success = await client.connect(model, resumeConfig, apiKey);
      if (!success) {
        console.error("DEBUG: Resume connection failed.");
        setConnected(false);
      }
    } catch (e) {
      console.error("DEBUG: Resume connection error:", e);
      setConnected(false);
    }
    setResuming(false);

  }, [client, sessionResumptionToken, apiKey, voiceName, conversationStartTime, language, countryCode, model, disconnect]);


  const onGoAway = useCallback((event: any) => {
    console.log("DEBUG: Received GoAway message. Preparing to resume...");
    // The connection will close soon. We should prepare to resume if active.
    const lastActivity = client.lastUserActivity;
    const timeSinceLastActivity = (Date.now() - lastActivity) / 1000 / 60; // minutes

    if (timeSinceLastActivity < 5) { // Resume if active in last 5 minutes
      // We don't trigger resume here immediately, we wait for the close or do it preemptively?
      // The docs say "When you receive a goAway notification... you can start a new connection".
      // It is safer to start it now or immediately upon close.
      // Let's trigger it immediately to ensure continuity if possible, or wait for close.
      // Waiting for close is standard to avoid double connections.
      // However, we need to ensure we don't clear the token on close if we intend to resume.
    } else {
      console.log("DEBUG: User inactive for > 5 mins. Allowing session to end.");
      // Clear token so we don't auto-resume on close
      setSessionResumptionToken(null);
    }
  }, [client]);

  // Effect 1: Handle event listeners
  useEffect(() => {
    const onOpen = () => {
      setConnected(true);
      if (resuming) {
        console.log("DEBUG: Session resumed successfully.");
      }

      // Reset retry count after 5 seconds of stable connection
      if (connectionStableTimerRef.current) clearTimeout(connectionStableTimerRef.current);
      connectionStableTimerRef.current = setTimeout(() => {
        console.log("DEBUG: Connection stable. Resetting retry count.");
        retryCountRef.current = 0;
      }, 5000);
    };
    const onClose = (event: CloseEvent) => {
      console.log("DEBUG: Connection closed.", event);

      // Clear stable timer
      if (connectionStableTimerRef.current) {
        clearTimeout(connectionStableTimerRef.current);
        connectionStableTimerRef.current = null;
      }

      // STOP AUDIO STREAMER ON CLOSE to prevent ghost talking
      audioStreamerRef.current?.stop();

      let attemptingResume = false;

      // Auto-resume logic
      if (!isEndingConversation && sessionResumptionToken && conversationStartTime) {
        const lastActivity = client.lastUserActivity;
        const timeSinceLastActivity = (Date.now() - lastActivity) / 1000 / 60; // minutes
        const conversationDuration = (Date.now() - conversationStartTime) / 1000 / 60;

        if (timeSinceLastActivity < 5 && conversationDuration < 30) {
          if (retryCountRef.current < 5) {
            console.log(`DEBUG: Auto-resuming session... (Attempt ${retryCountRef.current + 1}/5)`);
            retryCountRef.current += 1;
            attemptingResume = true;
            setResuming(true);
            // Small delay to ensure cleanup?
            setTimeout(() => resume(), 1000);
          } else {
            console.log("DEBUG: Max retries reached. Stopping auto-resume.");
            // Optionally notify user or handle permanent failure here
            disconnect(); // Clean up state
          }
        } else {
          console.log("DEBUG: Not resuming. Inactive or time limit reached.");
        }
      }

      if (!attemptingResume) {
        setConnected(false);
      }
    };

    const onError = (error: ErrorEvent) => console.error("error", error);
    const stopAudioStreamer = () => audioStreamerRef.current?.stop();
    const onAudio = (data: ArrayBuffer) =>
      audioStreamerRef.current?.addPCM16(new Uint8Array(data));

    client
      .on("error", onError)
      .on("open", onOpen)
      .on("close", onClose)
      .on("interrupted", stopAudioStreamer)
      .on("audio", onAudio)
      .on("goaway", onGoAway)
      .on("sessionresumptionupdate", onSessionResumptionUpdate);

    return () => {
      if (connectionStableTimerRef.current) clearTimeout(connectionStableTimerRef.current);
      client
        .off("error", onError)
        .off("open", onOpen)
        .off("close", onClose)
        .off("interrupted", stopAudioStreamer)
        .off("audio", onAudio)
        .off("goaway", onGoAway)
        .off("sessionresumptionupdate", onSessionResumptionUpdate);
      // Do NOT disconnect here. Only detach listeners.
    };
  }, [client, onGoAway, onSessionResumptionUpdate, sessionResumptionToken, conversationStartTime, isEndingConversation, resume, resuming, disconnect]);

  // Effect 2: Handle connection lifecycle (disconnect on unmount)
  useEffect(() => {
    return () => {
      client.disconnect(false);
      // Use the local disconnect which properly cleans up audio
      // disconnect(); 
    };
  }, [client, disconnect]);

  useEffect(() => {
    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client, onToolCall]);

  const connect = useCallback(
    async (language: string, countryCode: string, voiceName: string, apiKey: string) => {
      setCurrentStage(0);
      setLanguage(language);
      setCountryCode(countryCode);
      setApiKey(apiKey);
      setVoiceName(voiceName);
      setConversationStartTime(Date.now()); // Start timer

      setIsResumedSession(false);
      retryCountRef.current = 0; // Reset retry count on fresh connect

      const updatedInstructions = get_first_stage_instructions(language, countryCode);
      console.log("DEBUG: Updated initial instructions:", updatedInstructions);

      const config: LiveConnectConfig = {
        responseModalities: [Modality.AUDIO],
        // Initial connection has no handle, but we can set handle: null explicitly or omit it.
        // We set sessionResumptionConfig to enable it.
        realtimeInputConfig: {
          automaticActivityDetection: {
            endOfSpeechSensitivity: EndSensitivity.END_SENSITIVITY_LOW,
            silenceDurationMs: 300,
          },
        },
        inputAudioTranscription: {},
        outputAudioTranscription: {},
        systemInstruction: { parts: [{ text: updatedInstructions }] },
        tools: [
          {
            functionDeclarations: [
              getStageInstructionsDeclaration,
              makeNoteDeclaration,
              suggestJobsDeclaration,
              endConversationDeclaration,
            ],
          },
        ],
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: voiceName } },
        },
      };

      setConfig(config);
      client.disconnect();
      // audioStreamerRef.current?.resume(); // Resume audio streamer on new connection
      console.log("Connecting to model: ", model)
      await client.connect(model, config, apiKey);
    },
    [client, model, isEndingConversation] // Removed 'config' from dependencies
  );


  return {
    client,
    config,
    setConfig,
    model,
    setModel,
    connected,
    connect,
    disconnect,
    volume,
    isResuming: resuming,
    isResumedSession,
    confirmEndConversation,
    resume
  };
}
